import React, { useState } from "react";
import { ChevronRight, Copy } from "lucide-react";
import { toast } from "sonner";
import { T, roleValue, fmtHuman, fmtMDY, ageFromDob, snakeWords, sentenceCase, prettyType, labFlagKind, PrettyValue, isComplexValue } from "./pdUtil";
import { IconChartDocuments } from "../RevqMenuIcons";
import TemplateFreeRead from "./TemplateFreeRead";
import SchemaRead from "./pdSchemaRead";

const META = new Set(["extraction_confidence", "extraction_failed", "extraction_warnings", "raw_text", "raw_text_full", "confidence", "should_process", "relevance", "indicator_profile", "reason"]);
const IDENTITY = new Set(["first_name", "last_name", "middle_name", "full_name", "patient_name", "patient_first_name", "patient_last_name", "name", "gov_first_name", "gov_last_name", "gov_middle_name", "gov_full_name"]);

/* small building blocks */
const Fact = ({ label, children, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10.5px] uppercase tracking-[.04em] mb-0.5" style={{ color: T.mute }}>{label}</div>
    <div className="text-[13px] font-semibold" style={{ color: T.ink }}>{children}</div>
  </div>
);
const Chip = ({ children }) => <span className="inline-flex items-center text-[11.5px] px-2 py-[3px] rounded-[7px]" style={{ background: T.soft, color: T.sub }}>{children}</span>;
const Mono = ({ label, value }) => {
  const copy = () => { navigator.clipboard?.writeText(String(value)); toast.success(`${label} copied`); };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[10.5px] uppercase tracking-[.04em] w-[62px]" style={{ color: T.mute }}>{label}</span>
      <span className="font-mono text-[12.5px] font-semibold" style={{ color: T.ink }}>{value}</span>
      <button type="button" onClick={copy} className="ml-auto inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: T.indigo }}><Copy size={11} /> Copy</button>
    </div>
  );
};
const Prose = ({ label, children }) => (
  <div>
    <div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>{label}</div>
    <div className="text-[13px] leading-relaxed whitespace-pre-wrap" style={{ color: T.ink }}>{children}</div>
  </div>
);
const Kicker = ({ children }) => <div className="text-[11px] font-bold uppercase tracking-[.05em] mb-1.5" style={{ color: T.faint }}>{children}</div>;
const CardBox = ({ children }) => <div className="rounded-[12px] p-4" style={{ border: `1px solid ${T.hair}`, background: T.rail }}>{children}</div>;

// slot recorder: get(keys) returns first non-empty and marks ALL aliases placed,
// including each key's gov_ variant (and the stripped form), so a key never shows twice.
function makeSlots(ex, placed) {
  return (keys) => {
    keys.forEach((k) => { placed.add(k); placed.add(k.startsWith("gov_") ? k.slice(4) : `gov_${k}`); });
    return roleValue(ex, keys);
  };
}
function renderList(v) { return Array.isArray(v) ? v : (v ? [v] : []); }

/* ───────── Government ID ───────── */
function GovId({ ex, placed }) {
  const g = makeSlots(ex, placed);
  const name = [g(["gov_first_name", "first_name"]), g(["gov_middle_name", "middle_name"]), g(["gov_last_name", "last_name"])].filter(Boolean).join(" ");
  const dob = g(["gov_date_of_birth", "date_of_birth", "dob", "birth_date"]); const age = ageFromDob(dob);
  const sex = g(["gov_gender", "gov_sex", "gender", "sex"]);
  const idt = snakeWords(g(["gov_id_type", "id_type"])); const auth = g(["gov_issuing_authority", "issuing_authority"]);
  const num = g(["gov_id_number", "id_number"]); const issued = g(["gov_issue_date", "issue_date"]); const exp = g(["gov_expiration_date", "expiration_date"]);
  // Address on one line: street, city, state ZIP
  const street = g(["gov_address", "address", "gov_street", "street"]); const city = g(["gov_city", "city"]); const st = g(["gov_state", "state"]); const zip = g(["gov_zip_code", "zip", "zip_code", "postal_code"]);
  const addr = [street, city, [st, zip].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  // Organization: used only if it duplicates the issuing authority
  const org = g(["gov_organization", "organization_name", "organization"]);
  if (!(org && String(org).trim().toLowerCase() === String(auth).trim().toLowerCase())) { placed.delete("gov_organization"); placed.delete("organization"); placed.delete("organization_name"); }
  const chips = [["Height", g(["gov_height"])], ["Eyes", g(["gov_eye_color"])], ["Hair", g(["gov_hair_color"])], ["Class", g(["gov_license_class"])], ["Restrictions", g(["gov_restrictions"])], ["Endorsements", g(["gov_endorsements"])], ["Organ donor", g(["gov_organ_donor"])]].filter(([, v]) => v !== "" && v != null);
  const expDate = exp ? new Date(exp) : null; const expired = expDate && !isNaN(expDate) && expDate < new Date();
  return (
    <CardBox>
      <Kicker>{[idt, auth].filter(Boolean).join(" · ") || "Government ID"}</Kicker>
      <div className="text-[20px] font-bold mb-3" style={{ color: T.ink }}>{name || "—"}</div>
      <div className="grid grid-cols-2 gap-3 mb-3">
        {dob ? <Fact label="Date of birth">{fmtMDY(dob)}{age != null ? ` · ${age}` : ""}</Fact> : null}
        {sex ? <Fact label="Sex">{snakeWords(sex)}</Fact> : null}
        {num ? <Fact label="ID number">{num}</Fact> : null}
        {issued ? <Fact label="Issued">{fmtHuman(issued)}</Fact> : null}
      </div>
      {addr ? <div className="text-[12.5px] mb-2" style={{ color: T.sub }}>{addr}</div> : null}
      {exp ? <div className="text-[12.5px] font-semibold mb-2" style={{ color: expired ? T.amberTxt : T.sub }}>{expired ? "● Expired " : "Valid until "}{fmtHuman(exp)}</div> : null}
      {chips.length ? <div className="flex flex-wrap gap-1.5">{chips.map(([l, v]) => <Chip key={l}>{l}: {snakeWords(v)}</Chip>)}</div> : null}
    </CardBox>
  );
}

/* ───────── Insurance card (id_card) ───────── */
function InsCard({ ex, placed }) {
  const g = makeSlots(ex, placed);
  const payer = g(["payer_name", "payer", "insurance"]); const plan = g(["plan_name", "plan_type"]);
  const member = g(["subscriber_name", "member_name"]); const memberId = g(["member_id", "subscriber_id", "policy_number"]);
  const group = g(["group_number"]); const medicare = g(["medicare_number"]); const medicaid = g(["medicaid_number"]); const eff = g(["effective_date"]);
  const kicker = medicare ? "Medicare card" : medicaid ? "Medicaid card" : "Insurance card";
  const copays = [["Primary", g(["copay_primary"])], ["Specialist", g(["copay_specialist"])], ["ER", g(["copay_emergency"])], ["Urgent", g(["copay_urgent_care"])], ["Rx generic", g(["copay_rx_generic"])], ["Rx brand", g(["copay_rx_brand"])], ["Rx specialty", g(["copay_rx_specialty"])]].filter(([, v]) => v !== "" && v != null);
  const rxbin = g(["rx_bin"]); const rxpcn = g(["rx_pcn"]); const rxgrp = g(["rx_group"]);
  return (
    <CardBox>
      <Kicker>{kicker}</Kicker>
      <div className="text-[20px] font-bold" style={{ color: T.ink }}>{payer || "—"}</div>
      {plan ? <div className="text-[13px] mb-3" style={{ color: T.sub }}>{plan}</div> : <div className="mb-3" />}
      <div className="grid grid-cols-2 gap-3 mb-3">
        {member ? <Fact label="Member">{member}</Fact> : null}
        {memberId ? <Fact label="Member ID">{memberId}</Fact> : null}
        {group ? <Fact label="Group">{group}</Fact> : null}
        {medicare ? <Fact label="Medicare number">{medicare}</Fact> : null}
        {medicaid ? <Fact label="Medicaid number">{medicaid}</Fact> : null}
        {eff ? <Fact label="Effective">{fmtHuman(eff)}</Fact> : null}
      </div>
      {copays.length ? <div className="flex flex-wrap gap-1.5 mb-3">{copays.map(([l, v]) => <Chip key={l}>{l}: {v}</Chip>)}</div> : null}
      {(rxbin || rxpcn || rxgrp) ? (
        <div className="rounded-[10px] p-3 space-y-1.5" style={{ background: T.soft }}>
          <div className="text-[10.5px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Pharmacy benefit</div>
          {rxbin ? <Mono label="RxBIN" value={rxbin} /> : null}
          {rxpcn ? <Mono label="RxPCN" value={rxpcn} /> : null}
          {rxgrp ? <Mono label="RxGroup" value={rxgrp} /> : null}
        </div>
      ) : null}
    </CardBox>
  );
}

/* ───────── Encounter note ───────── */
function Encounter({ ex, placed }) {
  const g = makeSlots(ex, placed);
  const type = g(["encounter_type", "visit_type"]); const cc = g(["chief_complaint"]);
  const date = g(["encounter_date", "date_of_service", "visit_date"]); const prov = g(["provider_name", "rendering_provider"]); const fac = g(["facility", "facility_name"]);
  const vitals = (ex.vitals && typeof ex.vitals === "object") ? ex.vitals : (ex.vital_signs && typeof ex.vital_signs === "object") ? ex.vital_signs : null; if (vitals) { placed.add("vitals"); placed.add("vital_signs"); }
  const hpi = g(["history_of_present_illness", "hpi"]);
  const ros = ex.review_of_systems && typeof ex.review_of_systems === "object" ? ex.review_of_systems : null; if (ros) placed.add("review_of_systems");
  const pe = ex.physical_exam && typeof ex.physical_exam === "object" ? ex.physical_exam : null; if (pe) placed.add("physical_exam");
  const dx = renderList(g(["diagnoses", "icd_codes"])); const proc = renderList(g(["procedures", "cpt_codes"]));
  const assess = g(["assessment"]); const fu = g(["follow_up"]); const instr = renderList(g(["instructions"])); const signed = g(["signed_by"]); const plan = g(["plan"]);
  const codeRow = (x, i) => { const code = x.code || x.icd || x.cpt || ""; const desc = x.description || x.desc || x.name || (typeof x === "string" ? x : ""); const extra = x.status || (x.units ? `${x.units} units` : ""); return <div key={i} className="flex items-center gap-2 text-[12.5px] py-0.5"><span className="font-mono font-semibold" style={{ color: T.ink }}>{code}</span><span style={{ color: T.sub }}>{desc}</span>{extra ? <span className="ml-auto" style={{ color: T.mute }}>{extra}</span> : null}</div>; };
  return (
    <div className="space-y-4">
      <div>
        <Kicker>{type || "Encounter note"}</Kicker>
        <div className="text-[18px] font-bold" style={{ color: T.ink }}>{cc || "Encounter"}</div>
        <div className="text-[12.5px]" style={{ color: T.sub }}>{[fmtHuman(date), prov, fac].filter(Boolean).join(" · ")}</div>
      </div>
      {vitals ? <div className="flex flex-wrap gap-2">{Object.entries(vitals).filter(([, v]) => v != null && v !== "").map(([k, v]) => <div key={k} className="rounded-[9px] px-3 py-2" style={{ background: T.soft }}><div className="text-[10px] uppercase" style={{ color: T.mute }}>{sentenceCase(k)}</div><div className="text-[15px] font-bold" style={{ color: T.ink }}>{String(v)}</div></div>)}</div> : null}
      {hpi ? <Prose label="History of present illness">{hpi}</Prose> : null}
      {ros ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Review of systems</div>{Object.entries(ros).filter(([, v]) => v).map(([k, v]) => <div key={k} className="text-[12.5px] py-0.5"><b style={{ color: T.ink }}>{sentenceCase(k)}:</b> <span style={{ color: T.sub }}>{String(v)}</span></div>)}</div> : null}
      {pe ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Physical exam</div>{Object.entries(pe).filter(([, v]) => v).map(([k, v]) => <div key={k} className="text-[12.5px] py-0.5"><b style={{ color: T.ink }}>{sentenceCase(k)}:</b> <span style={{ color: T.sub }}>{String(v)}</span></div>)}</div> : null}
      {dx.length ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Diagnoses</div>{dx.map((x, i) => codeRow(typeof x === "object" ? x : { description: x }, i))}</div> : null}
      {proc.length ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Procedures</div>{proc.map((x, i) => codeRow(typeof x === "object" ? x : { description: x }, i))}</div> : null}
      {assess ? <Prose label="Assessment">{assess}</Prose> : null}
      {(fu || instr.length || plan) ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Plan</div>{fu ? <div className="text-[13px] font-bold mb-1" style={{ color: T.ink }}>{fu}</div> : null}{typeof plan === "string" && plan ? <div className="text-[13px] mb-1" style={{ color: T.ink }}>{plan}</div> : null}{instr.map((x, i) => <div key={i} className="text-[12.5px] py-0.5" style={{ color: T.sub }}>• {typeof x === "string" ? x : (x.text || "")}</div>)}</div> : null}
      {signed ? <div className="text-[11.5px] pt-2" style={{ color: T.mute }}>Signed by {signed}</div> : null}
    </div>
  );
}

/* ───────── Lab result ───────── */
function Lab({ ex, placed }) {
  const g = makeSlots(ex, placed);
  const name = g(["test_name", "panel_name", "test", "name"]); const specimen = g(["specimen"]); const lab = g(["performing_lab", "lab_name"]);
  const collected = g(["collected_at", "collection_date"]); const resulted = g(["resulted_at", "result_date"]);
  const results = Array.isArray(ex.results) ? ex.results : Array.isArray(ex.lab_results) ? ex.lab_results : Array.isArray(ex.tests) ? ex.tests : Array.isArray(ex.result_items) ? ex.result_items : [];
  if (results.length) { ["results", "lab_results", "tests", "result_items"].forEach((k) => placed.add(k)); }
  const outside = results.filter((r) => labFlagKind(r.flag)).length;
  const showRef = results.some((r) => r.reference || r.range || r.reference_range);
  const facts = [["Ordering", g(["ordering_provider"])], ["Referring", g(["referring_provider"])], ["Order", g(["order_number"])], ["Accession", g(["accession", "accession_number"])], ["MRN at lab", g(["mrn", "lab_mrn"])]].filter(([, v]) => v !== "" && v != null);
  return (
    <div className="space-y-4">
      <div>
        <Kicker>Lab result · Final</Kicker>
        <div className="text-[18px] font-bold" style={{ color: T.ink }}>{name || "Lab result"}</div>
        <div className="text-[12.5px]" style={{ color: T.sub }}>{[specimen, lab].filter(Boolean).join(" · ")}</div>
      </div>
      <div className="flex gap-2">
        {collected ? <div className="rounded-[9px] px-3 py-2" style={{ background: T.soft }}><div className="text-[10px] uppercase" style={{ color: T.mute }}>Collected</div><div className="text-[13px] font-semibold" style={{ color: T.ink }}>{fmtHuman(collected)}</div></div> : null}
        {resulted ? <div className="rounded-[9px] px-3 py-2" style={{ background: T.soft }}><div className="text-[10px] uppercase" style={{ color: T.mute }}>Resulted</div><div className="text-[13px] font-semibold" style={{ color: T.ink }}>{fmtHuman(resulted)}</div></div> : null}
      </div>
      {results.length ? (
        <div>
          {outside ? <div className="text-[12px] font-semibold mb-1" style={{ color: T.amberTxt }}>{outside} of {results.length} outside range</div> : null}
          <div className="rounded-[10px] overflow-hidden" style={{ border: `1px solid ${T.hair}` }}>
            <div className="grid text-[10.5px] uppercase tracking-[.04em] px-3 py-1.5" style={{ gridTemplateColumns: showRef ? "1fr auto 1fr" : "1fr auto", background: T.soft, color: T.mute }}><span>Test</span><span>Result</span>{showRef ? <span>Reference</span> : null}</div>
            {results.map((r, i) => { const k = labFlagKind(r.flag); const c = k === "crit" ? T.red : k === "flag" ? T.amberTxt : T.ink; return (
              <div key={i} className="grid text-[12.5px] px-3 py-1.5 items-center" style={{ gridTemplateColumns: showRef ? "1fr auto 1fr" : "1fr auto", borderTop: `1px solid ${T.hair}` }}>
                <span style={{ color: T.sub }}>{r.test || r.name || r.test_name}</span>
                <span className="font-semibold text-right" style={{ color: c }}>{r.value ?? r.result}{r.unit ? ` ${r.unit}` : ""}{r.flag && k ? ` ${String(r.flag).toUpperCase()}` : ""}</span>
                {showRef ? <span className="text-right" style={{ color: T.mute }}>{r.reference || r.range || r.reference_range || ""}</span> : null}
              </div>
            ); })}
          </div>
        </div>
      ) : null}
      {facts.length ? <div className="grid grid-cols-2 gap-3">{facts.map(([l, v]) => <Fact key={l} label={l}>{v}</Fact>)}</div> : null}
    </div>
  );
}

/* ───────── Radiology ───────── */
function Radiology({ ex, placed }) {
  const g = makeSlots(ex, placed);
  const exam = g(["exam", "exam_name", "study", "study_name"]); const date = g(["exam_date", "study_date"]); const site = g(["site", "facility"]);
  const birads = g(["birads", "bi_rads", "bi_rads_score"]); const fu = g(["follow_up", "recommendation"]);
  const impression = renderList(g(["impression"])); const comparison = g(["comparison"]); const comp = g(["breast_composition"]);
  const accession = g(["accession", "accession_number"]); const mrn = g(["mrn"]); const findings = g(["findings"]); const history = g(["history", "clinical_history"]); const technique = g(["technique"]); const signed = g(["read_by", "signed_by", "radiologist"]);
  const alarm = ["0", "4", "5"].includes(String(birads));
  return (
    <div className="space-y-4">
      <div><Kicker>Radiology report</Kicker><div className="text-[18px] font-bold" style={{ color: T.ink }}>{exam || "Radiology report"}</div><div className="text-[12.5px]" style={{ color: T.sub }}>{[fmtHuman(date), site].filter(Boolean).join(" · ")}</div></div>
      {(birads !== "" || fu) ? <div className="rounded-[10px] p-3" style={{ border: `1px solid ${alarm ? T.amberDot : T.hair}`, background: alarm ? "#FFF7ED" : T.rail }}><div className="text-[13px] font-bold flex items-center gap-2" style={{ color: alarm ? T.amberTxt : T.ink }}>{alarm ? <span style={{ width: 7, height: 7, borderRadius: 999, background: T.amberDot }} /> : null}Assessment{birads !== "" ? ` · BI-RADS ${birads}` : ""}</div>{fu ? <div className="text-[12.5px] mt-1" style={{ color: alarm ? T.amberTxt : T.sub }}>{fu}</div> : null}</div> : null}
      {impression.length ? <div><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>Impression</div><ol className="list-decimal ml-5 text-[13px] space-y-0.5" style={{ color: T.ink }}>{impression.map((x, i) => <li key={i}>{typeof x === "string" ? x : (x.text || "")}</li>)}</ol></div> : null}
      {(comparison || comp || accession || mrn) ? <div className="grid grid-cols-2 gap-3">{comparison ? <Fact label="Comparison">{comparison}</Fact> : null}{comp ? <Fact label="Breast composition">{comp}</Fact> : null}{accession ? <Fact label="Accession">{accession}</Fact> : null}{mrn ? <Fact label="MRN at site">{mrn}</Fact> : null}</div> : null}
      {findings ? <Prose label="Findings">{findings}</Prose> : null}
      {history ? <Prose label="History">{history}</Prose> : null}
      {technique ? <Prose label="Technique">{technique}</Prose> : null}
      {signed ? <div className="text-[11.5px] pt-2" style={{ color: T.mute }}>Read and signed by {signed}</div> : null}
    </div>
  );
}

/* ───────── generic ───────── */
function Generic({ ex, placed, docType }) {
  const rows = Object.entries(ex || {}).filter(([k, v]) => !META.has(k) && !IDENTITY.has(k) && v != null && v !== "" && !(Array.isArray(v) && !v.length));
  rows.forEach(([k]) => placed.add(k));
  const simple = rows.filter(([, v]) => !isComplexValue(v));
  const complex = rows.filter(([, v]) => isComplexValue(v));
  return (
    <div>
      <Kicker>{prettyType(docType)} · read without a template</Kicker>
      <div className="space-y-1.5">{simple.map(([k, v]) => <div key={k} className="flex items-start gap-3 text-[13px]"><span className="w-[160px] shrink-0" style={{ color: T.mute }}>{sentenceCase(k)}</span><span className="font-semibold" style={{ color: T.ink }}>{snakeWords(v)}</span></div>)}</div>
      {complex.length ? <div className="space-y-3 mt-3">{complex.map(([k, v]) => <div key={k}><div className="text-[11px] font-bold uppercase tracking-[.04em] mb-1" style={{ color: T.faint }}>{sentenceCase(k)}</div><PrettyValue value={v} /></div>)}</div> : null}
    </div>
  );
}

/* footer folds */
function Fold({ title, children, testid }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button type="button" data-testid={testid} onClick={() => setOpen((o) => !o)} className="flex items-center gap-1 text-[12px] font-semibold py-1" style={{ color: T.faint }}>
        <ChevronRight size={13} style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .15s" }} />{title}
      </button>
      {open ? <div className="pl-4 pb-2">{children}</div> : null}
    </div>
  );
}

function Meds({ ex }) {
  const meds = Array.isArray(ex.medications) ? ex.medications : Array.isArray(ex.meds) ? ex.meds : [];
  const al = Array.isArray(ex.allergies) ? ex.allergies : [];
  const act = meds.filter((m) => m.status !== "discontinued"), stop = meds.filter((m) => m.status === "discontinued");
  const meta = [roleValue(ex, ["as_of_date"]) ? `As of ${fmtHuman(roleValue(ex, ["as_of_date"])) || roleValue(ex, ["as_of_date"])}` : "", roleValue(ex, ["source"]), roleValue(ex, ["prescriber"])].filter(Boolean).join(" · ");
  const d = (v) => fmtHuman(v) || v;
  const row = (m, off, i) => (
    <tr key={`${off ? "s" : "a"}${i}`} className={off ? "off" : ""}>
      <td><div style={{ fontWeight: 600, color: off ? undefined : T.ink }}>{m.name} <span style={{ fontWeight: 400, color: T.faint }}>{m.strength || ""}</span></div>{m.note ? <div className="text-[12px] mt-0.5" style={{ color: T.faint }}>{m.note}</div> : null}</td>
      <td>{m.frequency || "—"}</td><td style={{ color: T.faint }}>{m.route ? sentenceCase(m.route) : ""}</td>
      <td style={{ color: T.faint }}>{off ? (m.end_date ? `Stopped ${d(m.end_date)}` : "Stopped") : (m.start_date ? `Since ${d(m.start_date)}` : "")}</td>
    </tr>
  );
  return (
    <div className="flex flex-col gap-4" data-testid="pd-lens-meds">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[.06em]" style={{ color: T.faint }}>Medication summary</div>
        <div className="text-[22px] font-bold mt-1" style={{ color: T.ink, letterSpacing: "-.01em" }} data-testid="pd-meds-title">{act.length} active medication{act.length === 1 ? "" : "s"}</div>
        {meta ? <div className="text-[13px] mt-1" style={{ color: T.faint }}>{meta}</div> : null}
      </div>
      {al.length ? (
        <div className="flex items-center gap-2 flex-wrap rounded-[10px] px-3 py-2.5 bg-white" style={{ border: "1px solid #F2D4A6" }} data-testid="pd-meds-allergies">
          <span className="text-[11px] font-bold uppercase tracking-[.06em] inline-flex items-center gap-1.5" style={{ color: T.amberTxt }}><span style={{ width: 6, height: 6, borderRadius: 999, background: T.amberDot }} />Allergies</span>
          {al.map((a, i) => <span key={i} className="text-[12.5px] rounded-full px-2.5 py-[3px]" style={{ border: `1px solid ${T.hair}`, color: T.ink }}>{a.substance || a.name}{a.reaction ? <span style={{ color: T.faint }}> {a.reaction}</span> : null}</span>)}
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="pd-meds w-full" style={{ borderCollapse: "collapse" }}>
          <thead><tr><th>Medication · {act.length} active</th><th>How often</th><th>Route</th><th>Dates</th></tr></thead>
          <tbody>
            {act.map((m, i) => row(m, false, i))}
            {stop.length ? <tr className="sub"><td colSpan={4}>Stopped · {stop.length}</td></tr> : null}
            {stop.map((m, i) => row(m, true, i))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LensCard({ analysis }) {
  const ex = analysis?.extraction || {};
  const docType = analysis?.document_type || analysis?.classification?.document_type;
  const cls = analysis?.classification || {};
  const placed = new Set();
  let body; let templateFree = false;
  if (docType === "government_id") body = <GovId ex={ex} placed={placed} />;
  else if (docType === "id_card") body = <InsCard ex={ex} placed={placed} />;
  else if (docType === "encounter_note") body = <Encounter ex={ex} placed={placed} />;
  else if (docType === "lab_result") body = <Lab ex={ex} placed={placed} />;
  else if (docType === "radiology_report") body = <Radiology ex={ex} placed={placed} />;
  else if (docType === "medication_summary" || docType === "medication_list") body = <Meds ex={ex} />;
  else {
    // Non-bespoke type. The child renders the WHOLE extraction (never trimmed), so
    // claim every key up front to keep LensCard's "Also/Not on this document" empty.
    templateFree = true;
    Object.keys(ex).forEach((k) => { if (k !== "extraction_confidence") placed.add(k); });
    // A central/custom template WAS used → schema-driven read (tiers + template name),
    // so a templated medical extraction is never mislabeled "read without a template".
    // Only a genuinely template-free doc (e.g. a home BP log) falls to TemplateFreeRead.
    const prompt = analysis?.prompt_used || {};
    const hasTemplate = !!(prompt.prompt_id || prompt.prompt_name || analysis?.prompt_id || analysis?.prompt_name);
    body = hasTemplate
      ? <SchemaRead analysis={analysis} />
      : <TemplateFreeRead typeLabel={prettyType(docType)} extraction={ex} used={placed} />;
  }

  // folds
  const other = []; const empty = [];
  for (const [k, v] of Object.entries(ex)) {
    if (META.has(k) || IDENTITY.has(k) || placed.has(k)) continue;
    if (v == null || v === "" || (Array.isArray(v) && !v.length)) empty.push(k);
    else other.push([k, v]);
  }
  const conf = ex.extraction_confidence || {};
  const confLine = (() => {
    const req = conf.required_total ?? conf.required; const reqGot = conf.required_extracted;
    const opt = conf.optional_total; const optGot = conf.optional_extracted; const c = conf.confidence ?? conf.score;
    const parts = [];
    if (reqGot != null && req != null) parts.push(`${reqGot === req ? "all " : ""}${req} required fields`);
    if (optGot != null && opt != null) parts.push(`${optGot} of ${opt} optional`);
    if (c != null) parts.push(`confidence ${c}`);
    return parts.length ? `How Lens read it · ${parts.join(" · ")}` : null;
  })();

  return (
    <div className="rounded-[14px] p-5" style={{ background: T.card, border: `1px solid ${T.hair}` }} data-testid="pd-lens-card">
      {templateFree ? null : <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[.05em] mb-3" style={{ color: T.faint }}><IconChartDocuments size={13} /> Read by Lens</div>}
      {cls.should_process === false ? <div className="text-[12.5px] font-semibold mb-3 flex items-center gap-1.5" style={{ color: T.amberTxt }}>● Lens didn't fully process this{cls.reason ? ` · ${cls.reason}` : ""}</div> : null}
      {body}
      {other.length ? (() => {
        const LONG = 80;
        const longText = other.filter(([, v]) => typeof v === "string" && v.length > LONG);
        const rest = other.filter(([, v]) => !(typeof v === "string" && v.length > LONG));
        const simple = rest.filter(([, v]) => !isComplexValue(v));
        const complex = rest.filter(([, v]) => isComplexValue(v));
        return (
        <div className="mt-4" data-testid="pd-also-on-doc">
          <div className="text-[11px] font-bold uppercase tracking-[.05em] mb-2" style={{ color: T.faint }}>Also on this document</div>
          {simple.length ? (
            <div className="grid gap-x-4 gap-y-2.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))" }}>
              {simple.map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <div className="text-[11.5px] mb-0.5" style={{ color: T.faint }}>{sentenceCase(k)}</div>
                  <div className="text-[13px] break-words" style={{ color: T.ink }}>{snakeWords(v)}</div>
                </div>
              ))}
            </div>
          ) : null}
          {longText.length ? (
            <div className="space-y-3 mt-3">
              {longText.map(([k, v]) => (
                <div key={k} data-testid={`pd-field-${k}`}>
                  <div className="text-[11.5px] mb-1" style={{ color: T.faint }}>{sentenceCase(k)}</div>
                  <p className="text-[13px] leading-relaxed whitespace-pre-wrap m-0" style={{ color: T.ink, maxWidth: "68ch" }}>{v}</p>
                </div>
              ))}
            </div>
          ) : null}
          {complex.length ? (
            <div className="space-y-3 mt-3">
              {complex.map(([k, v]) => (
                <div key={k} data-testid={`pd-field-${k}`}>
                  <div className="text-[11.5px] font-semibold mb-1" style={{ color: T.faint }}>{sentenceCase(k)}</div>
                  <PrettyValue value={v} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
        );
      })() : null}
      <div className="mt-4 pt-3 space-y-1" style={{ borderTop: `1px solid ${T.hair}` }}>
        {empty.length ? <Fold title={`Not on this document · ${empty.length}`} testid="pd-fold-empty"><div className="flex flex-wrap gap-1.5">{empty.map((k) => <span key={k} className="text-[11.5px]" style={{ color: T.mute }}>{sentenceCase(k)}</span>)}</div></Fold> : null}
        {confLine ? <div className="text-[11px] pt-1" style={{ color: T.mute }}>{confLine}</div> : null}
      </div>
    </div>
  );
}
